import React, { useState, useEffect } from "react";
import { Box, Text, Spinner } from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";

// Register chart.js modules
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const DataWavaxVwap = () => {
  const [vwapData, setVwapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = "https://blindfoldcapital.com/api/ta/trade_signals";

  const fetchVwapValues = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Network response was not ok");

      const result = await response.json();

      // Extract all VWAP data, assuming the newest are at the end
      const vwapEntries = result.data
        .filter((entry) => entry.vwap !== null)
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // Ensure oldest to newest

      setVwapData(vwapEntries); // Keep the natural order (oldest → newest)
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVwapValues();

    // Fetch data every 2 minutes
    const intervalId = setInterval(fetchVwapValues, 120000);

    return () => clearInterval(intervalId); // Cleanup interval
  }, []);

  if (loading) return <Spinner />;
  if (error) return <Text>Error: {error}</Text>;

  // Most recent VWAP value
  const mostRecentVwap = vwapData.length > 0 ? vwapData[vwapData.length - 1] : null;

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      year: "2-digit",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: "America/Chicago",
    });
  };

  const mostRecentVwapValue = mostRecentVwap ? mostRecentVwap.vwap.toFixed(2) : "N/A";
  const mostRecentVwapTimestamp = mostRecentVwap
    ? formatDate(mostRecentVwap.timestamp)
    : "N/A";

  // Prepare chart data (oldest → newest)
  const chartLabels = vwapData.map((entry) => formatDate(entry.timestamp));
  const chartDataValues = vwapData.map((entry) => entry.vwap);

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "VWAP Values",
        data: chartDataValues,
        borderColor: "silver",
        backgroundColor: "rgba(255, 165, 0, 0.2)",
        tension: 0.3, // Smooth line
        fill: true,
        pointRadius: 3,
        pointHoverRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `VWAP: ${context.raw.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: {
        type: "category",
        ticks: {
          maxTicksLimit: 12, // Reduce x-axis ticks for readability
        },
        title: {
          display: true,
          text: "Time",
        },
      },
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: "VWAP Value",
        },
        ticks: {
          callback: (value) => value.toFixed(2),
        },
      },
    },
  };

  return (
    <Box
          p={2}
          backgroundColor="gray.50"
          borderRadius="md"
          boxShadow="md"
          mt={2}
          mb={2}
        >
      {mostRecentVwap && (
        <Box >
          <Text fontSize="sm">
            <strong>Most Recent VWAP:</strong> {mostRecentVwapValue} (as of {mostRecentVwapTimestamp})
          </Text>
        </Box>
      )}
      <Box width="100%" height="100%">
        <Line data={chartData} options={chartOptions} />
      </Box>
    </Box>
  );
};

export default DataWavaxVwap;
